/**
 * Profit & Loss calculation functions for the Covered Calls Tracker.
 *
 * Formulae:
 *   Unrealised P&L = ((Ps - Pe) * Q) + (Pr * Q - Po * Q) - C
 *   Realised P&L   = (Pr * Q) + ((Ss - Se) * Q) - (Ob * Q) - C
 *
 * Where:
 *   Ps = current stock price, Pe = entry stock price, Q = quantity
 *   Pr = premium received (per share), Po = current option price
 *   C  = total commissions
 *   Ss = stock sale price, Se = stock entry price
 *   Ob = option buyback cost (per share)
 */

import type { TradePosition } from '../types/entities';
import { OptionStatus, PutOutcome } from '../types/enums';

/** Line-item breakdown of P&L for display purposes. */
export interface PnLBreakdown {
  premiumReceived: number;
  stockGainLoss: number;
  netRollCostCredit: number;
  totalCommissions: number;
  protectivePutCost?: number; // put premium paid (net of sell-to-close proceeds); 0/omitted when no put
  dividendIncome?: number;    // credit; total cash dividends received; 0/omitted when no dividends
  netProfitLoss: number;
  isRealised: boolean;
}

/**
 * Rounds a number to 2 decimal places using banker's rounding
 * (Math.round with epsilon correction for floating point).
 */
export function roundTo2dp(value: number): number {
  return Math.round((value + Number.EPSILON) * 100) / 100;
}

/**
 * Total protective-put premium PAID for a position (a debit).
 *
 * Returns `protectivePut.premium × quantity` when a protective put exists,
 * otherwise 0. This is the gross cost of the long put before any
 * sell-to-close proceeds are netted off.
 */
export function putPremiumPaid(position: TradePosition): number {
  if (position.protectivePut === undefined) {
    return 0;
  }
  return position.protectivePut.premium * position.quantity;
}

/**
 * Net protective-put cost for a position (a debit subtracted from P&L).
 *
 * = round2(putPremiumPaid − proceeds), where proceeds are the sell-to-close
 * sale proceeds (`saleProceedsPerShare × quantity`) only when the put's
 * outcome is CLOSED and proceeds are recorded. For ACTIVE and EXPIRED puts,
 * proceeds are 0, so the full premium paid remains a cost. Returns 0 when the
 * position has no protective put (backward compatible — no effect on P&L).
 */
export function protectivePutCost(position: TradePosition): number {
  const put = position.protectivePut;
  if (put === undefined) {
    return 0;
  }
  const proceeds =
    put.outcome === PutOutcome.CLOSED && put.saleProceedsPerShare != null
      ? put.saleProceedsPerShare * position.quantity
      : 0;
  return roundTo2dp(putPremiumPaid(position) - proceeds);
}

/** Line shape the P&L functions need from a dividend record. */
export interface DividendAmount {
  amount: number;
}

/**
 * Total dividend income for a position (a credit added to P&L).
 *
 * The arithmetic sum of each record's already-rounded `amount`, rounded again to
 * cents. Returns exactly 0 for an empty or omitted list, making the credit a no-op
 * for dividend-free positions (byte-for-byte backward compatible).
 */
export function dividendIncome(dividends?: DividendAmount[]): number {
  if (!dividends || dividends.length === 0) {
    return 0;
  }
  return roundTo2dp(dividends.reduce((sum, d) => sum + d.amount, 0));
}

/**
 * Calculates unrealised P&L for an open position.
 *
 * Formula: ((Ps - Pe) * Q) + (Pr * Q - Po * Q) - C
 *
 * Returns undefined if required market data is missing.
 */
export function calculateUnrealisedPnL(position: TradePosition, dividends?: DividendAmount[]): number | undefined {
  const { lastStockPrice, lastOptionPrice, purchasePrice, quantity, currentOption, totalCommissions, cumulativePremiumPerShare } = position;

  // Cannot calculate without current stock price
  if (lastStockPrice === undefined) {
    return undefined;
  }

  const Ps = lastStockPrice;
  const Pe = purchasePrice;
  const Q = quantity;
  // Use cumulative premium (historic + current active) when available
  // When cumulativePremiumPerShare is set, it contains all expired option premiums;
  // add the current active option premium on top.
  // When not set (pre-migration data), fall back to current option premium only.
  let Pr: number;
  if (cumulativePremiumPerShare !== undefined) {
    const activePremium = (currentOption?.status === OptionStatus.ACTIVE) ? (currentOption.premium ?? 0) : 0;
    Pr = cumulativePremiumPerShare + activePremium;
  } else {
    Pr = currentOption?.premium ?? 0;
  }
  // The current option is only a liability while it is ACTIVE. Once it has
  // expired (or been closed), there is nothing to buy back, so the option
  // price does not count — even if a stale lastOptionPrice is still persisted.
  const Po = (currentOption?.status === OptionStatus.ACTIVE) ? (lastOptionPrice ?? 0) : 0;
  const C = totalCommissions;

  const unrealised = ((Ps - Pe) * Q) + (Pr * Q - Po * Q) - C - protectivePutCost(position) + dividendIncome(dividends);
  return roundTo2dp(unrealised);
}

/**
 * Calculates realised P&L for a closed position.
 *
 * Formula: (Pr * Q) + ((Ss - Se) * Q) - (Ob * Q) - C
 *
 * Returns undefined if required exit data is missing.
 */
export function calculateRealisedPnL(position: TradePosition, dividends?: DividendAmount[]): number | undefined {
  const { exitStockPrice, purchasePrice, quantity, currentOption, exitOptionBuybackCost, totalCommissions, cumulativePremiumPerShare } = position;

  // Cannot calculate without exit stock price
  if (exitStockPrice === undefined) {
    return undefined;
  }

  // Use cumulative premium (all historic options) when available
  const Pr = cumulativePremiumPerShare ?? (currentOption?.premium ?? 0);
  const Ss = exitStockPrice;
  const Se = purchasePrice;
  const Q = quantity;
  const Ob = exitOptionBuybackCost ?? 0;
  const C = totalCommissions;

  const realised = (Pr * Q) + ((Ss - Se) * Q) - (Ob * Q) - C - protectivePutCost(position) + dividendIncome(dividends);
  return roundTo2dp(realised);
}

/** Option history entry shape needed for cumulative premium calculation. */
export interface OptionHistoryPremium {
  premium: number;
  buybackCost?: number;
}

/**
 * Generates a line-item P&L breakdown for a position.
 *
 * When optionHistory is provided, premiumReceived is the cumulative net premium
 * from all historical options plus any current active option premium (all × quantity).
 * Without optionHistory, only the current option premium is used (backward compatible).
 *
 * For open (unrealised) positions:
 *   - premiumReceived: cumulative net premium income * Q
 *   - stockGainLoss: (Ps - Pe) * Q
 *   - netRollCostCredit: -(Po * Q) (current option liability)
 *   - totalCommissions: C
 *   - netProfitLoss: sum of items
 *
 * For closed (realised) positions:
 *   - premiumReceived: cumulative net premium income * Q
 *   - stockGainLoss: (Ss - Se) * Q
 *   - netRollCostCredit: -(Ob * Q) (buyback cost)
 *   - totalCommissions: C
 *   - netProfitLoss: sum of items
 */
export function calculatePnLBreakdown(
  position: TradePosition,
  optionHistory?: OptionHistoryPremium[],
  dividends?: DividendAmount[],
): PnLBreakdown | undefined {
  const isRealised = position.exitStockPrice !== undefined;
  const Q = position.quantity;

  // Calculate cumulative premium per share from option history
  // Option history entries record per-share premiums and buyback costs
  let cumulativePremiumPerShare = 0;
  if (optionHistory && optionHistory.length > 0) {
    for (const entry of optionHistory) {
      cumulativePremiumPerShare += entry.premium - (entry.buybackCost ?? 0);
    }
    // Add current active option premium if not already in history
    // (active options are not yet in history)
    if (position.currentOption && position.currentOption.status === OptionStatus.ACTIVE) {
      cumulativePremiumPerShare += position.currentOption.premium;
    }
  } else if (position.cumulativePremiumPerShare !== undefined) {
    // No per-option history was supplied (e.g. the Trades list, which works
    // from the position record alone). Use the persisted cumulative premium —
    // which already includes all prior/rolled options net of buybacks — and add
    // the active option's premium on top, since an active option is not yet
    // folded into cumulativePremiumPerShare. Mirrors calculateUnrealisedPnL so
    // the list and detail screens agree on total premium income.
    const activePremium =
      position.currentOption && position.currentOption.status === OptionStatus.ACTIVE
        ? position.currentOption.premium
        : 0;
    cumulativePremiumPerShare = position.cumulativePremiumPerShare + activePremium;
  } else {
    // Last resort for pre-migration records that have neither option history
    // nor a persisted cumulativePremiumPerShare: current option premium only.
    cumulativePremiumPerShare = position.currentOption?.premium ?? 0;
  }

  if (isRealised) {
    const Ss = position.exitStockPrice!;
    const Se = position.purchasePrice;
    const Ob = position.exitOptionBuybackCost ?? 0;
    const C = position.totalCommissions;

    const premiumReceived = roundTo2dp(cumulativePremiumPerShare * Q);
    const stockGainLoss = roundTo2dp((Ss - Se) * Q);
    const netRollCostCredit = roundTo2dp(-(Ob * Q));
    const totalCommissions = roundTo2dp(C);
    const putCost = protectivePutCost(position);
    const dividendCredit = dividendIncome(dividends);
    const netProfitLoss = roundTo2dp(premiumReceived + stockGainLoss + netRollCostCredit - totalCommissions - putCost + dividendCredit);

    return {
      premiumReceived,
      stockGainLoss,
      netRollCostCredit,
      totalCommissions,
      protectivePutCost: putCost,
      dividendIncome: dividendCredit,
      netProfitLoss,
      isRealised: true,
    };
  }

  // Unrealised breakdown
  if (position.lastStockPrice === undefined) {
    return undefined;
  }

  const Ps = position.lastStockPrice;
  const Pe = position.purchasePrice;
  // Only an ACTIVE option is a buy-back liability; an expired/closed option
  // contributes zero even if a stale lastOptionPrice remains on the record.
  const Po = (position.currentOption?.status === OptionStatus.ACTIVE)
    ? (position.lastOptionPrice ?? 0)
    : 0;
  const C = position.totalCommissions;

  const premiumReceived = roundTo2dp(cumulativePremiumPerShare * Q);
  const stockGainLoss = roundTo2dp((Ps - Pe) * Q);
  const netRollCostCredit = roundTo2dp(-(Po * Q));
  const totalCommissions = roundTo2dp(C);
  const putCost = protectivePutCost(position);
  const dividendCredit = dividendIncome(dividends);
  const netProfitLoss = roundTo2dp(premiumReceived + stockGainLoss + netRollCostCredit - totalCommissions - putCost + dividendCredit);

  return {
    premiumReceived,
    stockGainLoss,
    netRollCostCredit,
    totalCommissions,
    protectivePutCost: putCost,
    dividendIncome: dividendCredit,
    netProfitLoss,
    isRealised: false,
  };
}
